import type { SectionProps } from "../../components/Questionnaire/Section";
import { type Options } from "../../components/Questionnaire/Section/Question/QuestionFactory/types";
import type {
  Answer,
  PartialQuestionOrGroup,
  QuestionProps,
} from "../../components/Questionnaire/Section/Question/types";
import type { RuleSet } from "../../components/Questionnaire/validation/form/types";
import { type ACTIONS } from "../questionnaire/reducer/constants";

export type Section = SectionProps & {
  isVisited?: boolean;
};

export type Group = {
  kind:
    | "group"
    | "shadow"
    | "address"
    | "percent"
    | "personalised_cover"
    | "shadow_personalised_cover";
  names: string[];
  defaultNoAnswers?: Record<string, Answer>;
};

export type Groups = Record<string, Group>;

export type ShadowGroup = Group & { kind: "shadow" };

export const isShadowGroup = (g: Group | undefined): g is ShadowGroup =>
  Boolean(g && g.kind === "shadow");

export function isShadowQuestion(question: QuestionProps): boolean;
export function isShadowQuestion(questionName: string): boolean;
export function isShadowQuestion(
  questionOrName: QuestionProps | string,
): boolean {
  if (typeof questionOrName === "string") {
    return questionOrName.startsWith("Shadow::");
  }
  return questionOrName.name?.startsWith("Shadow::") ?? false;
}

export type AddressGroup = Group & { kind: "address"; selectedState?: string };

export const isAddressGroup = (g: Group | undefined): g is AddressGroup =>
  Boolean(g && g.kind === "address");

export type PercentKindGroup = Group & { kind: "percent" };

export const isPercentKindGroup = (
  g: Group | undefined,
): g is PercentKindGroup => Boolean(g && g.kind === "percent");

export type PartialSection = Omit<Section, "questions"> & {
  questions: Partial<
    QuestionProps & {
      answer: Answer;
      visible?: boolean;
      shouldUpdate?: boolean;
      presentationText?: string;
      options?: Options;
    }
  >[];
};

export type UpdateAnswerValue = Answer | Record<string, string>;

export type Answers = Record<string, Answer>;

export type AddressMode = "lookup" | "manual" | "none";
export type AddressModes = Record<string, AddressMode>;

export type AddressSyncConfig = {
  linkQuestionName: string;
  linkQuestionValue?: string;
  linkedQuestionName: string;
  syncMap: Record<string, string>;
};

export type QuestionnaireMode = "standard" | "stacked-sections";

export type QuestionnaireState = {
  config?: Record<string, unknown>;
  sections: Section[];
  groups: Groups;
  validations?: readonly RuleSet[];
  answers: Answers;
  answerTimestamps?: Record<string, number>;
  visibility?: Record<string, boolean>;
  errors?: Record<string, string>;
  asyncErrors: Record<string, string>;
  currentSectionIndex: number;
  lastViewedSectionIndex: number;
  isValid?: boolean;
  isEditing?: boolean;
  selectedQuestion?: string | null;
  changedQuestions?: string[];
  changedAssumptionGroupIndex?: number;
  confirmedAssumptionGroups?: Set<number>;
  addressModes?: AddressModes;
  serverVisibility?: Record<string, boolean>;
  mode: QuestionnaireMode;
};

export type UpdateAnswerPayload = {
  name: string;
  value: UpdateAnswerValue;
};

type UpdateAnswerAction = {
  type: typeof ACTIONS.UPDATE_ANSWER;
  payload: UpdateAnswerPayload;
};

type SetSectionsAction = {
  type: typeof ACTIONS.SET;
  payload: SectionProps[];
};

type SetValidationsAction = {
  type: typeof ACTIONS.SET_VALIDATIONS;
  payload: readonly RuleSet[];
};

type SetConfigAction = {
  type: typeof ACTIONS.SET_CONFIG;
  payload: Record<string, unknown>;
};

type SetErrorsAction = {
  type: typeof ACTIONS.SET_ERRORS;
  payload: Record<string, string>;
};

type SetErrorAction = {
  type: typeof ACTIONS.SET_ERROR;
  payload: { name: string; message: string };
};

type SetAsyncErrorAction = {
  type: typeof ACTIONS.SET_ASYNC_ERROR;
  payload: { name: string; message: string };
};

type ClearAsyncErrorAction = {
  type: typeof ACTIONS.CLEAR_ASYNC_ERROR;
  payload: string; // question name
};

type SetAddressModeAction = {
  type: typeof ACTIONS.SET_ADDRESS_MODE;
  payload: { name: string; mode: AddressMode };
};

type SetEditModeAction = {
  type: typeof ACTIONS.SET_EDIT_MODE;
  payload: boolean;
};

type MergeSectionsAction = {
  type: typeof ACTIONS.MERGE;
  payload: {
    sections: PartialSection[];
    requestStartTime: number;
  };
};

type NavigationAction = {
  type: typeof ACTIONS.NEXT_SECTION | typeof ACTIONS.PREVIOUS_SECTION;
};

type validateActionAction = {
  type: typeof ACTIONS.VALIDATE_FORM;
};

type RemoveErrorAction = {
  type: typeof ACTIONS.REMOVE_ERROR;
  payload: string; // question name
};

type ShowQuestionAction = {
  type: typeof ACTIONS.SHOW_QUESTION;
  payload: string; // question name
};

type EditQuestionAction = {
  type: typeof ACTIONS.EDIT_QUESTION;
  payload: {
    questionName: string;
    assumptionGroupIndex?: number;
  };
};

type AppendQuestionsAction = {
  type: typeof ACTIONS.APPEND_QUESTIONS;
  payload: PartialQuestionOrGroup[]; // questions or groups
};

type ConfirmAssumptionGroupAction = {
  type: typeof ACTIONS.CONFIRM_ASSUMPTION_GROUP;
  payload: number; // assumptionGroup
};

type SetCurrentSectionAction = {
  type: typeof ACTIONS.SET_CURRENT_SECTION;
  payload: number; // section index
};

type SetVisibleAction = {
  type: typeof ACTIONS.SET_VISIBLE;
  payload: string[]; // questionNames
};

type SetHiddenAction = {
  type: typeof ACTIONS.SET_HIDDEN;
  payload: string[]; // questionNames
};

type HydrateAction = {
  type: typeof ACTIONS.HYDRATE;
  payload: QuestionnaireState;
};

export type QuestionnaireAction =
  | UpdateAnswerAction
  | SetSectionsAction
  | SetValidationsAction
  | SetConfigAction
  | SetErrorsAction
  | SetErrorAction
  | SetAsyncErrorAction
  | ClearAsyncErrorAction
  | MergeSectionsAction
  | NavigationAction
  | validateActionAction
  | RemoveErrorAction
  | ShowQuestionAction
  | EditQuestionAction
  | AppendQuestionsAction
  | SetAddressModeAction
  | SetEditModeAction
  | ConfirmAssumptionGroupAction
  | SetCurrentSectionAction
  | SetVisibleAction
  | SetHiddenAction
  | HydrateAction;
